import * as React from "react"
import { BotonComentario } from "@/components/Comentarios"
import { useQuery } from "@tanstack/react-query"

import { DataState } from "@/components/DataState"
import { WorkPanel } from "@/components/WorkPanel"
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table"
import { useAuth } from "@/lib/auth"
import { formatAmount, formatDate } from "@/lib/format"
import { cn } from "@/lib/utils"

import { useBooksFilters } from "@/routes/books/context"
import { fetchIncomeStatement } from "@/routes/statements/api"
import { EmptyBook, StatementCheck, UnmappedWarning } from "@/routes/statements/StatementChecks"
import { BotonDescargar } from "@/components/BotonDescargar"
import { bookToApi } from "@/routes/books/mapping"
import { t } from "@/lib/i18n"

/**
 * Estado de resultados, en cascada.
 *
 * Cada seccion llega ya con el signo con que APORTA al resultado —ingresos en
 * positivo, costos y gastos en negativo— asi que la lectura de arriba abajo es
 * una suma continua. Alternar sumas y restas obliga a recordar el signo de cada
 * linea, y es donde se cuela el error de leer un margen al reves.
 *
 * Los subtotales van intercalados en su sitio, con doble raya, como en un
 * estado impreso: son el resultado de lo de ARRIBA, no encabezados de lo de
 * abajo.
 */
const SUBTOTAL_DESPUES: Record<string, { clave: string; etiqueta: string }[]> = {
  costo_de_ventas: [{ clave: "utilidad_bruta", etiqueta: "Utilidad bruta" }],
  depreciacion_amortizacion: [
    { clave: "utilidad_operativa", etiqueta: "Utilidad operativa" },
  ],
  resultado_financiero: [
    { clave: "antes_de_impuestos", etiqueta: "Utilidad antes de impuestos" },
  ],
  impuestos: [{ clave: "utilidad_neta", etiqueta: "Utilidad neta" }],
}

export function IncomeStatementPage() {
  const { book, from, to } = useBooksFilters()
  const { activeTenantId, activeEntityId } = useAuth()

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["income-statement", activeTenantId, book, from, to],
    queryFn: () => fetchIncomeStatement({ book, from, to }),
  })

  return (
    <div className="space-y-3">
      {data ? (
        <>
          <StatementCheck
            cuadra={data.comprobacion.cuadra}
            diferencia={
              data.comprobacion.utilidad_neta_por_cascada -
              data.comprobacion.utilidad_neta_por_suma_de_saldos
            }
            etiqueta={
              data.comprobacion.cuadra
                ? "La cascada y la suma de saldos dan el mismo resultado"
                : "La cascada NO coincide con la suma de saldos"
            }
            detalle={`Utilidad neta ${formatAmount(data.subtotales.utilidad_neta)}`}
          />
          <EmptyBook asientos={data.asientos} />
          <UnmappedWarning noMapeado={data.no_mapeado} />
        </>
      ) : null}

      <WorkPanel
        actions={
          <BotonDescargar
            recurso="estado-resultados"
            filtros={{ entity_id: activeEntityId, libro: bookToApi(book), desde: from, hasta: to }}
          />
        }
        title={t("Estado de resultados")}
        meta={
          data ? (
            <span className="text-2xs text-muted-foreground">
              <span className="code">{formatDate(data.desde)}</span> a{" "}
              <span className="code">{formatDate(data.hasta)}</span>
            </span>
          ) : null
        }
        footnote={t("El EBITDA sale de la utilidad operativa sumando de vuelta la depreciacion y la amortizacion. Los ingresos financieros no estan en Ingresos: van al resultado financiero, porque los intereses ganados no son venta.")}
      >
        <DataState
          loading={isLoading}
          error={error}
          onRetry={() => void refetch()}
          empty={false}
          columns={[40, 20]}
        >
          {data ? (
            <Table>
              <TableBody>
                {data.secciones.map((seccion) => (
                  <React.Fragment key={seccion.clave}>
                    <TableRow className="row-group hover:bg-muted/60">
                      <TableCell className="[&]:py-1 text-2xs font-semibold uppercase tracking-[0.07em]">
                        {seccion.etiqueta}
                      </TableCell>
                      <TableCell
                        className={cn(
                          "num w-40 [&]:py-1 font-semibold",
                          seccion.importe < 0 && "text-danger",
                          !seccion.importe && "num-zero",
                        )}
                      >
                        {seccion.importe ? formatAmount(seccion.importe) : "—"}
                      </TableCell>
                    </TableRow>

                    {seccion.cuentas.map((cuenta) => (
                      <TableRow key={`${seccion.clave}-${cuenta.codigo}`}>
                        <TableCell className="pl-6">
                          <span className="code mr-2 text-muted-foreground">{cuenta.codigo}</span>
                          <BotonComentario className="mr-1 align-middle" ancla={{ ambito: "general", referencia: `cuenta:${cuenta.codigo}`,
                                                    rotulo: `${cuenta.codigo} ${cuenta.nombre}` }} />
                          {cuenta.nombre}
                        </TableCell>
                        <TableCell
                          className={cn("num w-40", cuenta.importe < 0 && "text-danger")}
                        >
                          {formatAmount(cuenta.importe)}
                        </TableCell>
                      </TableRow>
                    ))}

                    {(SUBTOTAL_DESPUES[seccion.clave] ?? []).map((sub) => (
                      <TableRow key={sub.clave} className="hover:bg-transparent">
                        <TableCell className="rule-double text-2xs font-semibold uppercase tracking-[0.06em]">
                          {sub.etiqueta}
                        </TableCell>
                        <TableCell className="num-strong rule-double w-40">
                          {formatAmount(
                            data.subtotales[sub.clave as keyof typeof data.subtotales],
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </React.Fragment>
                ))}

                {/* El EBITDA va al pie y no en la cascada: es una MEDIDA, no un
                    escalon del estado. Meterlo entre los subtotales sugeriria
                    que la utilidad neta se deriva de el. */}
                <TableRow className="hover:bg-transparent">
                  <TableCell className="text-2xs uppercase tracking-[0.06em] text-muted-foreground">
                    {t("EBITDA")}<span className="ml-1.5 normal-case tracking-normal">
                      {t("(utilidad operativa antes de depreciacion y amortizacion)")}</span>
                  </TableCell>
                  <TableCell className="num w-40">
                    {formatAmount(data.subtotales.ebitda)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          ) : null}
        </DataState>
      </WorkPanel>
    </div>
  )
}
